import React, { FunctionComponent, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogTitle, Grid, Box, Typography, makeStyles } from '@material-ui/core';
import { useLocalization } from 'Hooks';
import { storeAccount, onUpdateCustomerSecurityImageRequest } from 'Stores/Account';
import { userStore } from 'Stores/User';
import { StyledButton } from './styles';

const useStyles = makeStyles({
  imageBox: {
    cursor: 'pointer',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '10px',
    transition: 'all 0.3s ease',
    width: '115px',
    height: '115px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    outline: 'none',
    '&:hover': {
      border: '3px solid #55d3ff',
      transform: 'scale(1.05)',
    },
    '&:focus, &:focus-visible': {
      outline: 'none',
      border: '3px solid #55d3ff',
      boxShadow: '0 0 0 3px rgba(85, 211, 255, 0.35)',
    },
  },
  imageBoxSelected: {
    border: '3px solid #55d3ff',
  },
});

// Import security images
import securityImage1 from 'Assets/Images/securityImage/1.png';
import securityImage2 from 'Assets/Images/securityImage/2.png';
import securityImage3 from 'Assets/Images/securityImage/3.png';
import securityImage4 from 'Assets/Images/securityImage/4.png';
import securityImage5 from 'Assets/Images/securityImage/5.png';
import securityImage6 from 'Assets/Images/securityImage/6.png';
import securityImage7 from 'Assets/Images/securityImage/7.png';
import securityImage8 from 'Assets/Images/securityImage/8.png';

const securityImagesBase = [
  { id: 1, src: securityImage1 },
  { id: 2, src: securityImage2 },
  { id: 3, src: securityImage3 },
  { id: 4, src: securityImage4 },
  { id: 5, src: securityImage5 },
  { id: 6, src: securityImage6 },
  { id: 7, src: securityImage7 },
  { id: 8, src: securityImage8 },
];

const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

type DialogSecurityImageProps = {};

const DialogSecurityImage: FunctionComponent<DialogSecurityImageProps> = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isSecurityImageDialogOpen } = useSelector(storeAccount);
  const { sessionToken, customerNumber } = useSelector(userStore);
  const localization = useLocalization();
  const [selectedImageId, setSelectedImageId] = useState<number | null>(null);

  const securityImages = useMemo(() => shuffleArray(securityImagesBase), [isSecurityImageDialogOpen]);

  const handleImageClick = (imageId: number) => {
    setSelectedImageId(imageId);
  };

  const handleConfirm = () => {
    if (selectedImageId && sessionToken && customerNumber) {
      dispatch(
        onUpdateCustomerSecurityImageRequest({
          CustomerNumber: String(customerNumber),
          SessionToken: sessionToken,
          SecurityImageId: selectedImageId,
        })
      );
    }
  };

  return (
    <Dialog
      open={isSecurityImageDialogOpen}
      maxWidth="sm"
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Typography variant="h6" component="h1">
            {localization?.web_security_image_title || 'Güvenlik Resmi Seçiniz'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" component="p" color="textSecondary">
            {localization?.web_security_image_description ||
              'Hesap güvenliğiniz için size uygun bir güvenlik resmi seçiniz.'}
          </Typography>
        </Box>
        <Grid container component="ul" spacing={3}>
          {securityImages.map((image) => (
            <Grid component="li" item xs={6} sm={3} key={image.id}>
              <Box
                role="button"
                tabIndex={0}
                aria-pressed={selectedImageId === image.id}
                aria-label={`${image.id}. güvenlik resmi`}
                onClick={() => handleImageClick(image.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleImageClick(image.id); } }}
                className={`${classes.imageBox} ${selectedImageId === image.id ? classes.imageBoxSelected : ''}`}
              >
                <img
                  src={image.src}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: '100px',
                    height: '100px',
                    display: 'block',
                    borderRadius: '4px',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box mt={3} mb={2}>
          <StyledButton
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedImageId}
            className="primary-button"
            fullWidth
          >
            {localization?.confirm || 'Onayla'}
          </StyledButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DialogSecurityImage;
